import React, { useState, useEffect } from 'react';
import { AlertCircle, Download, RefreshCw, Search, Calendar, X, FileText, XCircle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
// import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select'; // Removed - using native select
import { cn } from '../../lib/utils';
import { Badge } from '../../components/ui/badge';
import { useToast } from '../../components/ui/use-toast';
import { getApiUrl, getServerName, getSystemID, getLocalApiUrl, authFetch } from '../../config/api';

const UnassignedTransactions = () => {
  const { toast } = useToast();
  const [failureLogs, setFailureLogs] = useState([]);
  const [filteredLogs, setFilteredLogs] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [touchPoints, setTouchPoints] = useState(['ALL']); // Start with default, will load from API
  const [isLoadingTouchPoints, setIsLoadingTouchPoints] = useState(false);
  
  // Client transactions modal state
  const [showClientTransactionsModal, setShowClientTransactionsModal] = useState(false);
  const [clientTransactions, setClientTransactions] = useState([]);
  const [clientTransactionsLoading, setClientTransactionsLoading] = useState(false);
  const [clientTransactionsError, setClientTransactionsError] = useState(null);
  const [selectedFailureTransaction, setSelectedFailureTransaction] = useState(null);
  
  // Pagination state
  const [pagination, setPagination] = useState({
    currentPage: 1,
    pageSize: 50, // Default page size
    totalCount: 0,
    hasMorePages: false
  });
  
  const [searchFilters, setSearchFilters] = useState({
    touchPoint: 'ALL',
    documentType: '',
    failureReason: '',
    transactionType: '',
    searchText: ''
  });

  // Helper function to format local date as YYYY-MM-DD
  const formatLocalDate = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const [dateRange, setDateRange] = useState({
    fromDate: (() => {
      const date = new Date();
      date.setDate(date.getDate() - 1); // Previous day
      return formatLocalDate(date); // Use local date instead of UTC
    })(),
    toDate: formatLocalDate(new Date()) // Use local date instead of UTC
  });

  // Load touchpoints on component mount
  useEffect(() => {
    fetchTouchPoints();
  }, []);

  // Utility function to format date to yyyy-MM-ddTHH:mm:ss
  const formatDateForAPI = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    const seconds = String(date.getSeconds()).padStart(2, '0');
    
    return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}`;
  };

  // Get date range from state
  const getDateRange = () => {
    const fromDate = new Date(dateRange.fromDate);
    fromDate.setHours(0, 0, 0, 0);
    
    const toDate = new Date(dateRange.toDate);
    toDate.setHours(23, 59, 59, 999);
    
    return {
      logFromTime: formatDateForAPI(fromDate),
      logToTime: formatDateForAPI(toDate)
    };
  };

  // Distinct device types from failure logs (matches /api/transactions/failures touchPoint filter)
  const fetchTouchPoints = async () => {
    setIsLoadingTouchPoints(true);
    try {
      const response = await authFetch(getLocalApiUrl('/api/transactions/failure-device-types'));
      const json = await response.json();
      if (!response.ok || !json.success) {
        throw new Error(json.error || `Request failed (${response.status})`);
      }
      const list = Array.isArray(json.data) ? json.data : ['ALL'];
      setTouchPoints(list.length ? list : ['ALL']);
    } catch (error) {
      console.error('Error fetching touch points:', error);
      setTouchPoints(['ALL']);
      toast({
        title: 'Touch points unavailable',
        description: 'Could not load device types from failure logs. Filter shows ALL only.',
        variant: 'destructive',
      });
    } finally {
      setIsLoadingTouchPoints(false);
    }
  };

  // API call to fetch client transactions
  const fetchClientTransactions = async (transactionId) => {
    setClientTransactionsLoading(true);
    setClientTransactionsError(null);

    try {
      // Try the external Rex API first
      const requestBody = {
        ServerName: getServerName(),
        SystemID: getSystemID(),
        Purpose: "GETCLIENTTRANSACTIONS",
        Source: "RXPROFILEWEB",
        DeviceType: "RXPROFILEWEB",
        TransactionID: transactionId
      };

      console.log('GetClientTransactions Request:', requestBody);

      const response = await fetch(getApiUrl('GET_CLIENT_TRANSACTIONS'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(requestBody)
      });

      if (!response.ok) {
        throw new Error(`API request failed: ${response.status} ${response.statusText}`);
      }

      const data = await response.json();
      console.log('GetClientTransactions Response:', data);

      if (data.ResponseCode === '0' || data.ResponseCode === '00') {
        setClientTransactions(data.ClientTransactions || []);
      } else {
        // Fall back to local DB API
        console.log('Rex API response code not successful, falling back to local API');
        await fetchClientTransactionsFromLocal(transactionId);
      }
    } catch (error) {
      console.error('Error fetching client transactions from Rex API:', error);
      // Fall back to local DB API
      await fetchClientTransactionsFromLocal(transactionId);
    } finally {
      setClientTransactionsLoading(false);
    }
  };

  // Fallback: fetch client transactions from local DB-backed API
  const fetchClientTransactionsFromLocal = async (transactionId) => {
    try {
      const response = await authFetch(
        getLocalApiUrl(`/api/transactions/client-transactions/${encodeURIComponent(transactionId)}`)
      );

      if (response.ok) {
        const result = await response.json();
        if (result.success) {
          setClientTransactions(result.data || []);
          console.log('Using local API client transactions data');
          return;
        }
      }
      // If local also fails, just set empty
      setClientTransactions([]);
      console.log('Local API also failed, showing empty client transactions');
    } catch (localError) {
      console.error('Error fetching client transactions from local API:', localError);
      setClientTransactions([]);
    }
  };

  // Handle clicking on a failure log row
  const handleRowClick = (failure) => {
    setSelectedFailureTransaction(failure);
    setShowClientTransactionsModal(true);
    fetchClientTransactions(failure.id);
  };

  // Close the modal
  const closeModal = () => {
    setShowClientTransactionsModal(false);
    setSelectedFailureTransaction(null);
    setClientTransactions([]);
    setClientTransactionsError(null);
  };

  // Fallback: fetch failure logs from local DB-backed API
  const fetchFailureLogsFromLocal = async () => {
    try {
      const params = new URLSearchParams({
        page: pagination.currentPage.toString(),
        pageSize: pagination.pageSize.toString()
      });

      if (searchFilters.touchPoint && searchFilters.touchPoint !== 'ALL') {
        params.set('touchPoint', searchFilters.touchPoint);
      }
      if (searchFilters.documentType) params.set('documentType', searchFilters.documentType);
      if (searchFilters.failureReason) params.set('failureReason', searchFilters.failureReason);
      if (searchFilters.transactionType) params.set('transactionType', searchFilters.transactionType);
      if (searchFilters.searchText) params.set('searchText', searchFilters.searchText);

      const dateRangeValues = getDateRange();
      if (dateRangeValues.logFromTime) params.set('fromDate', dateRangeValues.logFromTime);
      if (dateRangeValues.logToTime) params.set('toDate', dateRangeValues.logToTime);

      const response = await authFetch(
        getLocalApiUrl(`/api/transactions/failures?${params.toString()}`)
      );

      if (!response.ok) {
        throw new Error(`Local API request failed: ${response.status}`);
      }

      const result = await response.json();

      if (result.success) {
        const logs = result.data.map(item => ({
          ...item,
          application: item.operation || 'Unknown',
          failureType: item.errorDetails || 'Unknown Failure',
          action: 'View Details'
        }));
        setFailureLogs(logs);
        setFilteredLogs(filterLogs(logs));
        setPagination(prev => ({
          ...prev,
          totalCount: result.totalCount,
          hasMorePages: result.hasMorePages
        }));
        console.log('Using local API failure logs data');
        return;
      }
    } catch (localError) {
      console.error('Error fetching failure logs from local API:', localError);
    }
    // If local API also fails, set empty state
    setFailureLogs([]);
    setFilteredLogs([]);
    setPagination(prev => ({ ...prev, totalCount: 0, hasMorePages: false }));
  };

  // API call to fetch failure logs
  const fetchFailureLogs = async () => {
    setIsLoading(true);
    try {
      const dateRangeForAPI = getDateRange();
      const requestBody = {
        ServerName: getServerName(),
        SystemID: getSystemID(),
        TouchPoint: searchFilters.touchPoint,
        LogFromTime: dateRangeForAPI.logFromTime,
        Purpose: "GETPROFILEFAILEDTRANSACTIONS",
        LogToTime: dateRangeForAPI.logToTime,
        DocumentType: searchFilters.documentType,
        FailureReason: searchFilters.failureReason,
        TransactionType: searchFilters.transactionType,
        Source: "RXPROFILEWEB",
        DeviceType: "RXPROFILEWEB",
        PageNumber: pagination.currentPage.toString(),
        PageSize: pagination.pageSize.toString()
      };

      console.log('GetProfileFailedTransactions Request:', requestBody);

      const response = await fetch(getApiUrl('GET_PROFILE_FAILED_TRANSACTIONS'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(requestBody)
      });

      if (!response.ok) {
        throw new Error(`API request failed: ${response.status} ${response.statusText}`);
      }

      const data = await response.json();
      console.log('GetProfileFailedTransactions Response:', data);

      if (data.ResponseCode === '0' || data.ResponseCode === '00') {
        // Map API response to frontend format
        const mappedLogs = data.OutputData.map((item, index) => ({
          id: item.TransactionId || `FL-${item.TimeStamp}-${index}`,
          timestamp: item.TimeStamp || item.Timestamp || new Date().toISOString(),
          deviceType: item.Touchpoint || item.TouchPoint || 'Unknown',
          operation: item.Operation || item.TransactionPurpose || 'Unknown',
          verificationPurpose: item.VerificationPurpose || 'N/A',
          identificationType: item.IdentificationType || 'Unknown',
          application: item.Operation || item.TransactionPurpose || 'Unknown',
          failureType: item.FailureReason || 'Unknown Failure',
          documentType: item.DocumentType || 'Unknown Document',
          errorDetails: item.FailureDetails || 'No details available',
          impact: item.TransactionStatus || 'FAILED',
          action: 'View Details'
        }));
        setFailureLogs(mappedLogs);
        setFilteredLogs(filterLogs(mappedLogs));

        // Update pagination info from API response
        const inferredHasMorePages = data.HasMorePages !== undefined
          ? data.HasMorePages
          : mappedLogs.length >= pagination.pageSize;

        setPagination(prev => {
          let estimatedTotalCount;
          if (data.TotalCount) {
            estimatedTotalCount = data.TotalCount;
          } else if (inferredHasMorePages) {
            estimatedTotalCount = (prev.currentPage * prev.pageSize) + 1;
          } else {
            estimatedTotalCount = ((prev.currentPage - 1) * prev.pageSize) + mappedLogs.length;
          }

          return {
            ...prev,
            totalCount: estimatedTotalCount,
            hasMorePages: inferredHasMorePages
          };
        });
      } else {
        // Fall back to local DB API
        console.log('Rex API response code not successful, falling back to local API');
        await fetchFailureLogsFromLocal();
      }
    } catch (error) {
      console.error('Error fetching failure logs from Rex API:', error);
      // Fall back to local DB API
      await fetchFailureLogsFromLocal();
    } finally {
      setIsLoading(false);
    }
  };

  // Filter logs based on search text
  const filterLogs = (logs) => {
    if (!searchFilters.searchText.trim()) {
      return logs;
    }

    const searchTerm = searchFilters.searchText.toLowerCase();
    return logs.filter(log =>
      (log.errorDetails || '').toLowerCase().includes(searchTerm) ||
      (log.operation || '').toLowerCase().includes(searchTerm) ||
      (log.verificationPurpose || '').toLowerCase().includes(searchTerm) ||
      (log.identificationType || '').toLowerCase().includes(searchTerm) ||
      (log.failureType || '').toLowerCase().includes(searchTerm) ||
      (log.deviceType || '').toLowerCase().includes(searchTerm) ||
      (log.application || '').toLowerCase().includes(searchTerm) ||
      (log.documentType || '').toLowerCase().includes(searchTerm)
    );
  };

  // Pagination functions
  const handlePageChange = (newPage) => {
    setPagination(prev => ({ ...prev, currentPage: newPage }));
  };

  const handlePageSizeChange = (newPageSize) => {
    setPagination(prev => ({ ...prev, pageSize: newPageSize, currentPage: 1 }));
  };

  // Search function
  const handleSearch = () => {
    setPagination(prev => ({ ...prev, currentPage: 1 })); // Reset to first page on new search
    fetchFailureLogs();
  };

  // Update filtered logs when search text changes
  useEffect(() => {
    const filtered = filterLogs(failureLogs);
    setFilteredLogs(filtered);
    
    // Reset to first page when search changes
    if (searchFilters.searchText.trim()) {
      setPagination(prev => ({ ...prev, currentPage: 1 }));
    }
  }, [searchFilters.searchText, failureLogs]);

  // Fetch data when pagination changes
  useEffect(() => {
    fetchFailureLogs();
  }, [pagination.currentPage, pagination.pageSize]);

  // Load data on component mount
  useEffect(() => {
    fetchFailureLogs();
  }, []);

  // Function to truncate error details
  const truncateErrorDetails = (details, maxLength = 12) => {
    if (!details) return 'N/A';
    return details.length > maxLength ? `${details.substring(0, maxLength)}...` : details;
  };

  // Add formatTimestamp function
  const formatTimestamp = (timestamp) => {
    if (!timestamp) return 'N/A';
    try {
      const date = new Date(timestamp);
      const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 
                     'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      
      const day = date.getDate().toString().padStart(2, '0');
      const month = months[date.getMonth()];
      const year = date.getFullYear();
      const hours = date.getHours().toString().padStart(2, '0');
      const minutes = date.getMinutes().toString().padStart(2, '0');
      const seconds = date.getSeconds().toString().padStart(2, '0');
      
      return `${day}/${month}/${year} ${hours}:${minutes}:${seconds}`;
    } catch {
      return timestamp;
    }
  };

  // Format timestamp with milliseconds for client transactions
  const formatTimestampWithMilliseconds = (timestamp) => {
    if (!timestamp) return 'N/A';
    try {
      const date = new Date(timestamp);
      const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 
                     'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      
      const day = date.getDate().toString().padStart(2, '0');
      const month = months[date.getMonth()];
      const year = date.getFullYear();
      const hours = date.getHours().toString().padStart(2, '0');
      const minutes = date.getMinutes().toString().padStart(2, '0');
      const seconds = date.getSeconds().toString().padStart(2, '0');
      const milliseconds = date.getMilliseconds().toString().padStart(3, '0');
      
      return `${day} ${month} ${year} ${hours}:${minutes}:${seconds}.${milliseconds}`;
    } catch {
      return timestamp;
    }
  };

  const getStatusColor = (status) => {
    switch (status.toLowerCase()) {
      case 'high impact':
        return 'bg-red-100 text-red-800';
      case 'medium impact':
        return 'bg-yellow-100 text-yellow-800';
      case 'low impact':
        return 'bg-green-100 text-green-800';
      case 'failed':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  // Export function for CSV
  const exportToCSV = () => {
    if (filteredLogs.length === 0) {
      toast({
        title: 'No Data',
        description: 'No failure logs available to export.',
        variant: 'destructive'
      });
      return;
    }

    // Define CSV headers
    const headers = [
      'Transaction ID',
      'Timestamp',
      'Touch Point',
      'Transaction Type',
      'Verification',
      'Identification Type',
      'Document Type',
      'Error Details',
      'Status'
    ];

    // Convert logs to CSV format
    const csvContent = [
      headers.join(','),
      ...filteredLogs.map(log => [
        `"${log.id}"`,
        `"${log.timestamp}"`,
        `"${log.deviceType}"`,
        `"${log.operation}"`,
        `"${log.verificationPurpose}"`,
        `"${log.identificationType}"`,
        `"${log.documentType}"`,
        `"${log.errorDetails.replace(/"/g, '""')}"`, // Escape quotes in error details
        `"${log.impact}"`
      ].join(','))
    ].join('\n');

    // Create and download file
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `failure-logs-${formatLocalDate(new Date())}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    toast({
      title: 'Export Successful',
      description: `Exported ${filteredLogs.length} failure logs to CSV.`
    });
  };

    return (
    <div className="p-6">
      {/* Search & Filter Section */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-medium text-gray-900">Search & Filter Unassigned Transactions</h3>
          <Button variant="outline" size="sm" onClick={exportToCSV}>
            <Download className="h-4 w-4 mr-2" />
            Export
          </Button>
        </div>
        
        {/* Date Range Section */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
          <div>
            <Label htmlFor="fromDate">From Date</Label>
            <Input
              id="fromDate"
              type="date"
              value={dateRange.fromDate}
              onChange={(e) => setDateRange({...dateRange, fromDate: e.target.value})}
              className="mt-1"
            />
          </div>
          <div>
            <Label htmlFor="toDate">To Date</Label>
            <Input
              id="toDate"
              type="date"
              value={dateRange.toDate}
              onChange={(e) => setDateRange({...dateRange, toDate: e.target.value})}
              className="mt-1"
            />
      </div>
          <div>
            <Label htmlFor="searchText">Search Text</Label>
            <Input
              id="searchText"
              type="text"
              placeholder="Search in error details..."
              value={searchFilters.searchText}
              onChange={(e) => setSearchFilters({...searchFilters, searchText: e.target.value})}
              className="mt-1"
            />
          </div>
          <div className="flex items-end">
            <Button onClick={handleSearch} disabled={isLoading} className="w-full">
              {isLoading ? (
                <>
                  <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                  Loading...
                </>
              ) : (
                <>
                  <Search className="h-4 w-4 mr-2" />
                  Search
                </>
              )}
            </Button>
          </div>
          </div>

        {/* Filter Section */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
          <div>
            <Label htmlFor="touchPoint">Touch Point</Label>
            <select
              id="touchPoint"
              value={searchFilters.touchPoint}
              onChange={(e) => setSearchFilters({...searchFilters, touchPoint: e.target.value})}
              disabled={isLoadingTouchPoints}
              className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white text-sm"
            >
              {touchPoints.map((touchPoint) => (
                <option key={touchPoint} value={touchPoint}>
                  {touchPoint}
                </option>
              ))}
            </select>
          </div>
          <div>
            <Label htmlFor="documentType">Document Type</Label>
            <Input
              id="documentType"
              type="text"
              placeholder="e.g., Driver License, Passport"
              value={searchFilters.documentType}
              onChange={(e) => setSearchFilters({...searchFilters, documentType: e.target.value})}
              className="mt-1"
            />
          </div>
          <div>
            <Label htmlFor="failureReason">Failure Reason</Label>
            <Input
              id="failureReason"
              type="text"
              placeholder="e.g., OCR Failure, Face Detection"
              value={searchFilters.failureReason}
              onChange={(e) => setSearchFilters({...searchFilters, failureReason: e.target.value})}
              className="mt-1"
            />
          </div>
          <div>
            <Label htmlFor="transactionType">Transaction Type</Label>
            <Input
              id="transactionType"
              type="text"
              placeholder="e.g., OnBoard, Verify"
              value={searchFilters.transactionType}
              onChange={(e) => setSearchFilters({...searchFilters, transactionType: e.target.value})}
              className="mt-1"
            />
        </div>
      </div>

        
        {/* Failure Logs Table */}
        <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                  <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Timestamp
                </th>
                  <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Transaction ID
                </th>
                  <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Touch Point
                </th>
                  <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Transaction Type
                  </th>
                  <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Verification
                  </th>
                  <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Identification Type
                </th>
                  <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Document Type
                </th>
                  <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Error Details
                </th>
                  <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
                {filteredLogs.length > 0 ? (
                  filteredLogs.map((failure) => (
                    <tr 
                      key={failure.id} 
                      className="hover:bg-gray-50 cursor-pointer"
                      onClick={() => handleRowClick(failure)}
                    >
                      <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-900">
                        {formatTimestamp(failure.timestamp)}
                      </td>
                      <td className="px-3 py-4 whitespace-nowrap text-sm font-mono text-gray-900">
                        {failure.id}
                      </td>
                      <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-900">
                        {failure.deviceType}
                      </td>
                      <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-900">
                        <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded text-xs font-medium">
                          {failure.operation}
                        </span>
                      </td>
                      <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-900">
                        {failure.verificationPurpose}
                      </td>
                      <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-900">
                        <span className="px-2 py-1 bg-purple-100 text-purple-800 rounded text-xs">
                          {failure.identificationType}
                        </span>
                      </td>
                      <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-900">
                        {failure.documentType}
                      </td>
                      <td className="px-3 py-4 text-sm text-gray-900">
                        <div 
                          className="cursor-pointer" 
                          title={failure.errorDetails}
                        >
                          {truncateErrorDetails(failure.errorDetails)}
                        </div>
                      </td>
                      <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-900">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(failure.impact)}`}>
                          {failure.impact}
                        </span>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={9} className="px-3 py-12 text-center text-gray-500">
                      <div className="flex flex-col items-center">
                        <AlertCircle className="h-12 w-12 text-gray-400 mb-4" />
                        <p className="text-lg font-medium">No failure logs found</p>
                        <p className="text-sm">Try adjusting your search criteria or date range</p>
                    </div>
                  </td>
                  </tr>
                )}
              </tbody>
            </table>
                        </div>
          
          {/* Pagination Controls */}
          {pagination.totalCount > 0 && (
            <div className="px-3 py-4 border-t border-gray-200 bg-gray-50">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  <div className="text-sm text-gray-700">
                    Showing {((pagination.currentPage - 1) * pagination.pageSize) + 1} to{' '}
                    {Math.min(pagination.currentPage * pagination.pageSize, pagination.totalCount)} of{' '}
                    {pagination.totalCount} results
                        </div>
                  <div className="flex items-center space-x-2">
                    <label className="text-sm text-gray-700">Page Size:</label>
                    <select
                      value={pagination.pageSize}
                      onChange={(e) => handlePageSizeChange(Number(e.target.value))}
                      className="border border-gray-300 rounded px-2 py-1 text-sm"
                    >
                      <option value={25}>25</option>
                      <option value={50}>50</option>
                      <option value={100}>100</option>
                      <option value={200}>200</option>
                    </select>
                        </div>
                      </div>
                
                <div className="flex items-center space-x-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handlePageChange(pagination.currentPage - 1)}
                    disabled={pagination.currentPage <= 1}
                  >
                    Previous
                  </Button>
                  
                  <span className="text-sm text-gray-700">
                    Page {pagination.currentPage} of {Math.ceil(pagination.totalCount / pagination.pageSize)}
                  </span>
                  
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handlePageChange(pagination.currentPage + 1)}
                    disabled={!pagination.hasMorePages}
                  >
                    Next
                  </Button>
                      </div>
                    </div>
                    </div>
          )}
        </div>
      </div>

      {/* Client Transactions Modal */}
      {showClientTransactionsModal && selectedFailureTransaction && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={closeModal}>
          <div className="bg-white rounded-lg shadow-xl max-w-6xl w-full mx-4 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex justify-between items-center">
                    <div>
                <h3 className="text-lg font-semibold text-gray-900">Unassigned Transactions</h3>
                <p className="text-sm text-gray-600">Transaction ID: {selectedFailureTransaction.id}</p>
              </div>
              <button 
                onClick={closeModal}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X className="h-6 w-6" />
              </button>
            </div>
            
            <div className="p-6">
              {clientTransactionsLoading && (
                <div className="flex items-center justify-center py-12">
                  <div className="text-center">
                    <RefreshCw className="h-8 w-8 text-blue-500 animate-spin mx-auto mb-4" />
                    <p className="text-gray-600">Loading client transactions...</p>
                  </div>
                      </div>
              )}
              
              {clientTransactionsError && (
                <div className="rounded-lg p-4 mb-4 bg-red-50 border border-red-200">
                  <div className="flex items-center">
                    <XCircle className="h-5 w-5 text-red-500 mr-2" />
                    <p className="text-red-800">{clientTransactionsError}</p>
                      </div>
                    </div>
              )}
              
              {!clientTransactionsLoading && !clientTransactionsError && (
                <div>
                  <h5 className="text-lg font-semibold text-gray-900 mb-4">
                    Related Client Transactions ({clientTransactions.length})
                  </h5>
                  
                  {clientTransactions.length > 0 ? (
                    <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                      <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                          <thead className="bg-gray-50">
                            <tr>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Timestamp
                              </th>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Client Transaction ID
                              </th>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Transaction Type
                              </th>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Status
                              </th>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Details
                              </th>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Confidence Score
                              </th>
                            </tr>
                          </thead>
                          <tbody className="bg-white divide-y divide-gray-200">
                            {clientTransactions.map((clientTransaction, index) => (
                              <tr key={`${clientTransaction.ClientTransactionID}-${index}`} className="hover:bg-gray-50">
                                <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">
                                  {formatTimestampWithMilliseconds(clientTransaction.Timestamp)}
                                </td>
                                <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900 font-mono">
                                  {clientTransaction.ClientTransactionID || 'N/A'}
                                </td>
                                <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">
                                  <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded text-xs font-medium">
                                    {clientTransaction.ClientTransactionType || 'N/A'}
                                  </span>
                  </td>
                                <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">
                                  <span className={`px-2 py-1 rounded text-xs font-medium ${
                                    clientTransaction.Status?.toLowerCase() === 'success' 
                                      ? 'bg-green-100 text-green-800' 
                                      : 'bg-red-100 text-red-800'
                                  }`}>
                                    {clientTransaction.Status || 'N/A'}
                      </span>
                  </td>
                                <td className="px-4 py-4 text-sm text-gray-500 max-w-xs">
                                  <div className="whitespace-pre-line break-words">
                                    {clientTransaction.Details || 'N/A'}
                    </div>
                  </td>
                                <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">
                                  <span className="px-2 py-1 bg-gray-100 text-gray-800 rounded text-xs">
                                    {clientTransaction.ConfidenceScore || 'N/A'}
                                  </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
                  ) : (
                    <div className="text-center py-8">
                      <FileText className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                      <p className="text-gray-500 text-lg font-medium">No client transactions found</p>
                      <p className="text-gray-400 text-sm">No related transactions were found for this failure.</p>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UnassignedTransactions;